import { MedicareConsent } from '@/consent/medicare/domain/MedicareConsent';
import { MedicareConsentRequest } from '@/consent/medicare/domain/MedicareConsentRequest';
import { MedicareConsentResponse } from '@/consent/medicare/domain/MedicareConsentResponse';
import { MedicareConsentSaveAndExitRequest } from '@/consent/medicare/domain/MedicareConsentSaveAndExitRequest';
import { CONTACT_PREFERENCE_TYPES } from '@hfc/common';

export class MedicareConsentApiAdapter {

  public static entityToApiRequest (consentForm: MedicareConsent): MedicareConsentRequest {
    return {
      appointment_required: consentForm.appointment_required,
      medicare_representative: consentForm.agent ? consentForm.agent : null,
      signature: `${consentForm.signature.first} ${consentForm.signature.last}`,
      reference_id: consentForm.appointment_required && consentForm.appointment_id ? consentForm.appointment_id : null,
      appointment_scope: consentForm.appointment_scopes.map(s => ({ ...s, initials: s.initials.trim().toUpperCase() })),
      contact_preference: consentForm.appointment_required && consentForm.contact_preference !== undefined ? consentForm.contact_preference : null,
      agree_to_receive_text: consentForm.agree_to_receive_text,
      aep: consentForm.aep,
      contact_info: {
        first_name: consentForm.contactInfo.firstName,
        last_name: consentForm.contactInfo.lastName,
        email: consentForm.contactInfo.email,
        phone: Number(`1${consentForm.contactInfo.phone}`),
        date_of_birth: consentForm.contactInfo.dateOfBirth,
        zip_code: consentForm.contactInfo.zipCode,
        county: consentForm.contactInfo.county,
      }
    };
  }

  public static entityToSaveAndExitApiRequest (consentForm: MedicareConsent): MedicareConsentSaveAndExitRequest {
    return {
      appointment_required: consentForm.appointment_required,
      aep: consentForm.aep,
      contact_info: {
        first_name: consentForm.contactInfo.firstName,
        last_name: consentForm.contactInfo.lastName,
        email: consentForm.contactInfo.email,
        phone: Number(`1${consentForm.contactInfo.phone}`),
        date_of_birth: consentForm.contactInfo.dateOfBirth,
        zip_code: consentForm.contactInfo.zipCode,
        county: consentForm.contactInfo.county,
      },
      ...(consentForm.agent ? { medicare_representative: consentForm.agent } : {}),
      ...(consentForm.signature.first && consentForm.signature.last ? { signature: `${consentForm.signature.first} ${consentForm.signature.last}` } : {}),
      ...(consentForm.appointment_required && consentForm.appointment_id ? { reference_id: consentForm.appointment_id } : {}),
      ...(consentForm.appointment_scopes.length ? { appointment_scope: consentForm.appointment_scopes.map(s => ({ ...s, initials: s.initials.trim().toUpperCase() })) } : {}),
      ...(consentForm.appointment_required && consentForm.contact_preference ? { contact_preference: consentForm.contact_preference } : {}),
      ...(consentForm.agree_to_receive_text ? { agree_to_receive_text: consentForm.agree_to_receive_text } : {}),
    };
  }

  public static apiResponseToEntity (consentData: MedicareConsentResponse): MedicareConsent {
    return {
      id: consentData.id,
      contact_preference: consentData.contact_preference as CONTACT_PREFERENCE_TYPES || undefined,
      appointment_required: consentData.appointment_required,
      appointment_id: consentData.reference_id || '',
      agent: !consentData.medicare_representative || consentData.medicare_representative.key === 'default' ? '' : consentData.medicare_representative.key,
      agree_to_receive_text: consentData.agree_to_receive_text,
      appointment_scopes: [...consentData.appointment_scope],
      signature: {
        first: this.getSignatureParts(consentData.signature).first,
        last: this.getSignatureParts(consentData.signature).last,
      },
      contactInfo: {
        firstName: consentData.contact_info.first_name,
        lastName: consentData.contact_info.last_name,
        email: consentData.contact_info.email,
        phone: Number(consentData.contact_info.phone.toString().slice(1)),
        dateOfBirth: consentData.contact_info.date_of_birth,
        zipCode: consentData.contact_info.zip_code,
        county: consentData.contact_info.county,
      },
      invitee_id: '',
      captcha_token: '',
      aep: consentData.aep || false,
    };
  }

  private static getSignatureParts (signature: string): { first: string; last: string } {
    if (!signature) {
      return { first: '', last: '' };
    }

    const parts = signature.trim().split(' ');
    const first = parts.shift() || '';
    const last = parts.join(' ') || '';

    return { first, last };
  }

}
